import React, { useState } from 'react';
import studentService from '../../services/api/student';
import { useQuery, useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
import { 
  Key, 
  CheckCircle, 
  AlertCircle, 
  Gift, 
  Clock,
  ArrowLeft,
  BookOpen,
  Zap,
  ShieldCheck,
  ChevronLeft,
  Sparkles,
  Info,
  ArrowRight,
  Ticket
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';

const ActivateCode = () => {
  const navigate = useNavigate();
  const { openWhatsApp } = useSettings();
  const [formData, setFormData] = useState({
    code: '',
    courseId: ''
  });
  const toast = useToast();
  const [activatedCourse, setActivatedCourse] = useState(null);

  // جلب الكورسات المتاحة للطالب (المنطق الأصلي)
  const { data: courses, isLoading } = useQuery(
    ['available-courses', { purchasable: true }],
    () => studentService.getCourses({ purchasable: true }).then(data => {
      return data?.data?.courses || data?.courses || data || [];
    })
  );

  // تنفيذ عملية تفعيل الكود (Mutation)
  const activationMutation = useMutation(
    async (data) => {
      const response = await studentService.activateCode(data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message || 'تم تفعيل الكود بنجاح! استمتع بالتعلم');
        setActivatedCourse(data.course);
        setFormData({ code: '', courseId: '' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'كود غير صحيح أو منتهي الصلاحية');
      }
    }
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'code') {
      // تحويل الكود إلى أحرف كبيرة وتنسيقه (المنطق الأصلي)
      const formattedCode = value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
      setFormData(prev => ({ ...prev, [name]: formattedCode }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.code.trim()) return toast.error('يرجى إدخال كود التفعيل أولاً');
    if (!formData.courseId) return toast.error('يرجى اختيار الكورس المراد تفعيله');
    activationMutation.mutate(formData);
  };

  // دالة فتح واتساب مع الدعم الفني
  const handleWhatsAppSupport = () => {
    const message = 'مرحباً، أحتاج إلى مساعدة في تفعيل اشتراك بالكود.';
    openWhatsApp(message);
  };

  const formatCodeDisplay = (code) => {
    if (code.length <= 6) return code;
    const parts = code.split('-');
    if (parts.length === 3) return parts.join('-');
    if (code.length === 12) {
      return `COURSE-${code.slice(0, 4)}-${code.slice(4, 8)}`;
    }
    return code;
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري تهيئة بوابة التفعيل...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8 px-4 sm:px-6 py-10 min-h-screen bg-gray-50/30">
      
      {/* Header & Back Action */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in slide-in-from-top duration-500">
        <div className="space-y-1">
            <button
                onClick={() => navigate('/')}
                className="group flex items-center text-gray-400 hover:text-primary-600 font-bold transition-all mb-2 text-sm"
            >
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                العودة للرئيسية
            </button>
            <h1 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                <div className="bg-primary-600 text-white p-1.5 rounded-xl"><Zap size={24} fill="white" /></div>
                بوابة التفعيل الذكية
            </h1>
        </div>
      </div>

      {/* Success Success State (If activated) */}
      {activatedCourse && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-[2.5rem] p-8 lg:p-10 shadow-xl shadow-emerald-100/50 animate-in zoom-in duration-500 relative overflow-hidden">
          <Sparkles className="absolute top-4 right-4 text-emerald-200" size={40} />
          <div className="flex flex-col md:flex-row items-center gap-6 relative z-10">
            <div className="bg-white p-4 rounded-3xl shadow-lg text-emerald-600">
                <CheckCircle size={48} />
            </div>
            <div className="flex-1 text-center md:text-right">
              <h3 className="text-2xl font-black text-emerald-900 mb-2">تهانينا! تم تفعيل الكورس بنجاح 🎉</h3>
              <p className="text-emerald-700 font-medium text-lg leading-relaxed">
                لقد أصبح لديك الآن وصول كامل إلى محتوى <span className="font-black text-emerald-900 underline underline-offset-4">"{activatedCourse?.title || 'الكورس'}"</span>.
              </p>
            </div>
            <button
              onClick={() => navigate('/student/courses')}
              className="bg-emerald-600 text-white px-8 py-4 rounded-2xl font-black text-sm hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-100 transform active:scale-95 flex items-center gap-2"
            >
              ابدأ الدراسة الآن <ArrowLeft size={18} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left Column: Activation Form */}
        <div className="lg:col-span-7 space-y-6">
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-12 relative group">
            <div className="absolute top-0 left-0 w-2 h-full bg-primary-600 opacity-20 group-hover:opacity-100 transition-opacity"></div>
            
            <div className="mb-10 text-center md:text-right">
                <h2 className="text-2xl font-black text-gray-900 mb-2">أدخل رمز الاشتراك</h2>
                <p className="text-gray-400 font-medium">اختر الكورس ثم أدخل الكود المكون من ١٢ خانة والمستلم من الإدارة.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Course Selection */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                    <BookOpen size={14} className="text-primary-500" /> الكورس المطلوب تفعيله *
                </label>
                <div className="relative">
                    <select
                        name="courseId"
                        value={formData.courseId}
                        onChange={handleInputChange}
                        className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                        required
                    >
                        <option value="">اختر الكورس من القائمة...</option>
                        {courses?.map(course => {
                            const durationText = course.default_duration_days === 30 ? 'اشتراك شهري' :
                                              course.default_duration_days === 120 ? 'اشتراك ترم دراسي' :
                                              course.default_duration_days === 300 ? 'اشتراك عام دراسي' :
                                              `${course.default_duration_days} يوم`;
                            return (
                              <option key={course.id} value={course.id}>
                                {course.title} - ({durationText})
                              </option>
                            );
                          })}
                    </select>
                    <ChevronLeft size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 -rotate-90 pointer-events-none" />
                </div>
              </div>

              {/* Code Input */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                    <Ticket size={14} className="text-primary-500" /> رمز التفعيل (Activation Code) *
                </label>
                <div className="relative">
                    <input
                        type="text"
                        name="code"
                        value={formData.code}
                        onChange={handleInputChange}
                        placeholder="COURSE-XXXX-XXXX"
                        className="w-full px-6 py-5 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all text-xl font-black font-mono text-center tracking-[0.2em] placeholder:tracking-normal placeholder:text-gray-200"
                        maxLength={19}
                        required
                    />
                    <Key size={20} className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none" />
                </div>
                
                {/* Visual Code Preview */}
                {formData.code && (
                    <div className="flex items-center justify-center gap-2 py-3 px-4 bg-primary-50 rounded-xl border border-primary-100 animate-in fade-in slide-in-from-top-2">
                        <span className="text-[10px] font-black text-primary-400 uppercase">المعاينة النهائية:</span>
                        <span className="text-sm font-black text-primary-700 font-mono tracking-widest">{formatCodeDisplay(formData.code)}</span>
                    </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={activationMutation.isLoading}
                className="w-full py-5 bg-primary-600 text-white rounded-[2rem] font-black text-lg shadow-2xl shadow-primary-100 hover:bg-primary-700 transition-all transform active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3"
              >
                {activationMutation.isLoading ? (
                  <>
                    <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin" />
                    جاري التحقق من الرمز...
                  </>
                ) : (
                  <>
                    <Gift size={22} fill="white" />
                    تفعيل محتوى الكورس الآن
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Quick Guide Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
                { label: 'تفعيل فوري', desc: 'فتح الدروس بمجرد التأكد', icon: Clock, color: 'text-blue-500', bg: 'bg-blue-50' },
                { label: 'وصول شامل', desc: 'كافة الفيديوهات والملفات', icon: BookOpen, color: 'text-emerald-500', bg: 'bg-emerald-50' },
                { label: 'أمان البيانات', desc: 'تشفير كامل لعملية الدفع', icon: ShieldCheck, color: 'text-purple-500', bg: 'bg-purple-50' }
            ].map((f, i) => (
                <div key={i} className="bg-white p-6 rounded-3xl border border-gray-100 text-center space-y-3 shadow-sm group hover:shadow-lg transition-all">
                    <div className={`w-12 h-12 ${f.bg} ${f.color} rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform`}>
                        <f.icon size={24} />
                    </div>
                    <div>
                        <h4 className="font-black text-gray-900 text-sm">{f.label}</h4>
                        <p className="text-[10px] text-gray-400 font-bold uppercase mt-1">{f.desc}</p>
                    </div>
                </div>
            ))}
          </div>
        </div>

        {/* Right Column: Instructions & Help */}
        <div className="lg:col-span-5 space-y-6">
          
          {/* Visual Instructions Card */}
          <div className="bg-gray-900 rounded-[2.5rem] p-8 lg:p-10 text-white relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 right-0 w-40 h-40 bg-primary-600/20 rounded-full blur-3xl -z-0"></div>
            <div className="relative z-10">
                <h3 className="text-xl font-black mb-8 flex items-center gap-3">
                    <Info className="text-primary-400" />
                    كيف تحصل على كود التفعيل؟
                </h3>
                
                <div className="space-y-6">
                    {[
                        'اختر الكورس التعليمي الذي تريده',
                        'حول الرسوم (فودافون كاش أو إنستاباي)',
                        'ارفع إثبات الدفع في صفحة "تأكيد الدفع"',
                        'انتظر مراجعة الإدارة (أقل من ٢٤ ساعة)',
                        'استلم الكود الخاص بك عبر واتساب أو البريد',
                        'أدخل الكود في النموذج الجانبي وفعل الكورس'
                    ].map((step, idx) => (
                        <div key={idx} className="flex items-start gap-4 group">
                            <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 text-xs font-black text-primary-400 group-hover:bg-primary-600 group-hover:text-white transition-colors">
                                {idx + 1}
                            </div>
                            <p className="text-sm font-medium text-gray-300 leading-snug group-hover:text-white transition-colors">{step}</p>
                        </div>
                    ))}
                </div>

                <div className="mt-10 pt-8 border-t border-white/5">
                    <button 
                        onClick={() => navigate('/student/payment-confirmation')}
                        className="w-full py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2"
                    >
                        رفع إثبات دفع جديد <ArrowLeft size={14} />
                    </button>
                </div>
            </div>
          </div>

          {/* Help & Support Card */}
          <div className="bg-amber-50 rounded-[2rem] border border-amber-100 p-8 space-y-4">
            <div className="flex items-center gap-3 text-amber-800">
                <AlertCircle className="flex-shrink-0" />
                <h3 className="text-lg font-black tracking-tight leading-none uppercase">هل واجهت مشكلة؟</h3>
            </div>
            <div className="space-y-3">
                <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2"></div>
                    <p className="text-sm font-bold text-amber-700/80">تأكد أن الكود الذي أدخلته ينتمي للكورس المختار في القائمة.</p>
                </div>
                <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2"></div>
                    <p className="text-sm font-bold text-amber-700/80">الكود صالح للاستخدام مرة واحدة فقط وبحساب واحد فقط.</p>
                </div>
                <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2"></div>
                    <p className="text-sm font-bold text-amber-700/80">إذا ظهرت رسالة "كود مستخدم"، تواصل مع الدعم الفني فوراً.</p>
                </div>
            </div>
            <button 
              onClick={handleWhatsAppSupport}
              className="text-amber-800 font-black text-xs uppercase tracking-widest flex items-center gap-1 hover:gap-3 transition-all pt-2 hover:text-amber-900"
            >
              تواصل مع الدعم عبر واتساب <ChevronLeft size={14} />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ActivateCode;